import { useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

type TInfiniteScrollApiFunction<T = any> = {
  data: T[];
  nextPage: number | undefined;
  total?: number;
  totalUnseen?: number;
};

interface TUseInfiniteScrollProps<T = any> {
  queryKey: any[];
  apiFunction: (page: number) => Promise<TInfiniteScrollApiFunction<T>>;
  containerRef: React.RefObject<HTMLDivElement>;
  preventFetch?: boolean;
  startPage?: number;
  refetchInterval?: number;
  enabled?: boolean;
}

function useInfiniteScroll<T = any>({
  apiFunction,
  containerRef,
  preventFetch = false,
  startPage = 1,
  queryKey,
  refetchInterval,
  enabled = true,
}: TUseInfiniteScrollProps<T>) {
  const {
    isError,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetching,
    error,
    data,
    refetch,
  } = useInfiniteQuery<TInfiniteScrollApiFunction<T>>({
    queryKey,
    initialPageParam: startPage,
    queryFn: ({ pageParam }) => apiFunction(pageParam as number),
    getNextPageParam: (prevData) => prevData.nextPage,
    refetchInterval,
    enabled,
  });

  useEffect(() => {
    const container = containerRef.current;

    const handleScroll = () => {
      if (container) {
        const isAtBottom =
          container.scrollTop + container.clientHeight >=
          container.scrollHeight;

        if (isAtBottom && !preventFetch) {
          fetchNextPage();
        }
      }
    };

    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFunction, containerRef, preventFetch, isLoading]);

  const flattenedData = data?.pages.flatMap((item) => item.data);

  return {
    isLoading: isLoading,
    isLoadingMore: isFetching,
    data: flattenedData,
    isError,
    error: (error as any)?.message || null,
    fetchNextPage,
    refetch,
    hasNextPage: hasNextPage ?? true,
    isEmpty: flattenedData?.length === 0,
    totalUnseen: data?.pages[0].totalUnseen,
  };
}

export default useInfiniteScroll;
